<template>
  <dialog ref="dialog" class="work-lightbox motion-dialog" aria-label="Artwork image viewer" @close="restoreFocus" @cancel="close" @keydown.left.prevent="move(-1)" @keydown.right.prevent="move(1)" @click="backdrop">
    <div class="work-lightbox-toolbar">
      <span aria-live="polite">{{ index + 1 }} / {{ images.length }}</span>
      <button type="button" class="work-lightbox-close" @click="close" :aria-label="zh ? '關閉影像' : 'Close image'">{{ zh ? '關閉' : 'Close' }}<BrandIcon name="close" /></button>
    </div>
    <figure v-if="current" :key="index" :data-dir="dir">
      <img :src="current.src" :alt="current.alt || ''" />
      <figcaption>{{ current.caption || current.alt }}</figcaption>
    </figure>
    <div v-if="images.length > 1" class="work-lightbox-navigation">
      <button type="button" @click="move(-1)">{{ zh ? '上一張' : 'Previous' }} ←</button>
      <button type="button" @click="move(1)">{{ zh ? '下一張' : 'Next' }} →</button>
    </div>
  </dialog>
</template>
<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { pauseSmoothScroll, scrollPage } from '@/runtime/smooth-scroll'
import BrandIcon from '@/components/BrandIcon.vue'
const props = defineProps({ zh: Boolean })
const dialog = ref(null), images = ref([]), index = ref(0), dir = ref(0)
let opener = null, lock = null
const current = computed(() => images.value[index.value])
// 2026-09-19 哲宇：「點圖片出來的 popup，頁面滑下去的時候會看不到」。兩個原因疊在一起：
// ① Chromium 給 top-layer 的 dialog 是 position:absolute（相對初始包含塊，會跟著文件捲走），開啟時 focus 又把頁面拉回頂端；
// ② Lenis 把滾輪轉成程式捲動，body overflow:hidden 擋不住。
// 解：dialog 明說 position:fixed（永遠在視窗裡）＋開啟時停 Lenis 並鎖住捲動（body 定位保留原位置，關閉還原）。
function lockScroll() {
  const y = window.scrollY, html = document.documentElement, body = document.body
  lock = { y, html: html.style.overflow, position: body.style.position, top: body.style.top, width: body.style.width }
  pauseSmoothScroll(true)
  html.style.overflow = 'hidden'; body.style.position = 'fixed'; body.style.top = `-${y}px`; body.style.width = '100%'
}
function unlockScroll() {
  if (!lock) return
  const { y, html, position, top, width } = lock; lock = null
  document.documentElement.style.overflow = html; document.body.style.position = position; document.body.style.top = top; document.body.style.width = width
  pauseSmoothScroll(false)
  scrollPage(y)
}
async function open(list, start = 0, trigger = document.activeElement) {
  images.value = list; index.value = start; dir.value = 0; opener = trigger
  await nextTick()
  if (!dialog.value.open) { lockScroll(); dialog.value.showModal() }
}
function move(delta) { dir.value = delta; index.value = (index.value + delta + images.value.length) % images.value.length }
function restoreFocus() { unlockScroll(); if (opener?.isConnected) opener.focus({ preventScroll: true }) }
function close() { dialog.value?.close() }
function backdrop(event) { if (event.target === dialog.value) close() }
onBeforeUnmount(() => { if (dialog.value?.open) restoreFocus() })
defineExpose({ open })
</script>
<style>
.work-lightbox{position:fixed;inset:0;margin:auto;overscroll-behavior:contain;touch-action:none;width:min(96vw,1600px);max-width:none;height:calc(100dvh - 32px);max-height:none;padding:20px 24px;background:var(--bg0);color:#f2eee5;border:0;border-radius:var(--r-lg);box-shadow:0 24px 80px rgba(0,0,0,.7);overflow:hidden;font-family:var(--font-body)}.work-lightbox::backdrop{background:rgba(0,0,0,.94)}.work-lightbox[open]{display:flex;flex-direction:column}.work-lightbox-toolbar,.work-lightbox-navigation{display:flex;align-items:center;justify-content:space-between;gap:16px;flex:none;font-size:13px}.work-lightbox button{min-height:44px;padding:8px 18px;color:var(--ink-2);background:var(--tint-2);border:0;border-radius:var(--r-pill);cursor:pointer;transition:var(--t-hover)}.work-lightbox button:hover{background:var(--tint-3);color:var(--gold)}.work-lightbox button:focus-visible{outline:2px solid var(--gold);outline-offset:3px}.work-lightbox figure{flex:1;min-height:0;margin:12px 0;display:flex;flex-direction:column;align-items:center;gap:14px}.work-lightbox figure img{width:100%;height:100%;min-height:0;flex:1;object-fit:contain}.work-lightbox figcaption{flex:none;text-align:center;max-width:80ch;max-height:80px;overflow:auto;font-size:13px;line-height:1.5;color:#b8b5ad}@media(max-width:640px){.work-lightbox{width:100vw;height:100dvh;padding:12px;border-radius:0}.work-lightbox figure{margin:8px 0}.work-lightbox figcaption{font-size:12px}}
.work-lightbox-close{display:inline-flex;align-items:center;gap:8px}.work-lightbox-close .brand-icon{width:18px;height:18px}
/* 關閉記號：2023 品牌圖示 Close（2026-10-04 取代字元 ×，文字保留） */
</style>
